import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Fab,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import CloseIcon from '@mui/icons-material/Close';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { execute as executeCommand } from '../../../services/commandExecutor';
import flowsApi from '../../../services/flowsApiService';
import { flowDisabledUserMessage } from '../../../utils/flowEnabled';
import { waitingDisplayLabel, isFailedExecutionResult } from '../../../utils/executionStatusDisplay';
import { db } from '../../../services/firebase';
import { acceptCall, hangupCall, getActiveCallSnapshot } from '../../../services/voice/voiceCallService';
import { OPEN_CALL_PANEL_EVENT } from '../../../utils/chromeEvents';

const DEFAULT_BUTTONS = [
  { label: 'Unlock Door', flowId: 'flow1' },
  { label: 'Page', flowId: 'flow2' },
];

const normalizeButtons = (buttons) => {
  const source = Array.isArray(buttons) ? buttons.slice(0, 2) : [];
  return [0, 1].map((idx) => ({
    label: String(source[idx]?.label || DEFAULT_BUTTONS[idx].label),
    flowId: String(source[idx]?.flowId || DEFAULT_BUTTONS[idx].flowId),
  }));
};

const extractSipUser = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const sipMatch = raw.match(/^sip:([^@;>]+)/i);
  if (sipMatch?.[1]) return sipMatch[1].trim();
  const withoutScheme = raw.replace(/^sip:/i, '');
  return withoutScheme.split('@')[0].split(';')[0].trim();
};

export default function CallPanel({ tenantId, hideFab = false }) {
  const [open, setOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [callState, setCallState] = useState('idle');
  const [callContext, setCallContext] = useState({ from_user: '', to_user: '' });
  const [buttons, setButtons] = useState(DEFAULT_BUTTONS);
  const [flowOptions, setFlowOptions] = useState([]);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [runningFlowId, setRunningFlowId] = useState('');

  const fabColor = useMemo(() => {
    if (callState === 'incoming') return 'error';
    if (callState === 'active') return 'success';
    return 'primary';
  }, [callState]);

  const loadPanelConfig = useCallback(async () => {
    if (!tenantId) {
      setButtons(DEFAULT_BUTTONS);
      return;
    }

    setLoadingConfig(true);
    try {
      const configRef = doc(db, 'tenants', String(tenantId), 'settings', 'callPanel');
      const snap = await getDoc(configRef);
      if (snap.exists()) {
        const data = snap.data() || {};
        setButtons(normalizeButtons(data.buttons));
      } else {
        setButtons(DEFAULT_BUTTONS);
      }
    } catch (error) {
      console.error('[CallPanel] Failed to load config', error);
      setButtons(DEFAULT_BUTTONS);
    } finally {
      setLoadingConfig(false);
    }
  }, [tenantId]);

  const loadFlowOptions = useCallback(async () => {
    if (!tenantId) {
      setFlowOptions([]);
      return;
    }

    try {
      const flows = await flowsApi.getAll(String(tenantId));
      const normalized = (Array.isArray(flows) ? flows : [])
        .map((flow) => ({
          id: flow.flowId || flow.id,
          name: flow.flowName || flow.name || flow.flowId || flow.id,
        }))
        .filter((flow) => !!flow.id);
      setFlowOptions(normalized);
    } catch (error) {
      console.error('[CallPanel] Failed to load flow options', error);
      setFlowOptions([]);
    }
  }, [tenantId]);

  useEffect(() => {
    if (!open) return;
    loadPanelConfig();
    loadFlowOptions();
  }, [open, loadFlowOptions, loadPanelConfig]);

  useEffect(() => {
    const snapshot = getActiveCallSnapshot();
    if (snapshot.state === 'incoming') {
      setCallContext({
        from_user: extractSipUser(snapshot.detail?.from),
        to_user: extractSipUser(snapshot.detail?.to),
      });
      setCallState('incoming');
    } else if (snapshot.state === 'active') {
      setCallContext({
        from_user: extractSipUser(snapshot.detail?.from),
        to_user: extractSipUser(snapshot.detail?.to),
      });
      setCallState('active');
    }
  }, []);

  useEffect(() => {
    const onIncoming = (event) => {
      const detail = event?.detail || {};
      setCallContext({
        from_user: extractSipUser(detail.from_user || detail.from || detail.From),
        to_user: extractSipUser(detail.to_user || detail.to || detail.To),
      });
      setCallState('incoming');
    };

    const onActive = (event) => {
      const detail = event?.detail || {};
      if (detail.from_user || detail.to_user || detail.from || detail.to || detail.From || detail.To) {
        setCallContext({
          from_user: extractSipUser(detail.from_user || detail.from || detail.From),
          to_user: extractSipUser(detail.to_user || detail.to || detail.To),
        });
      }
      setCallState('active');
    };

    const onEnded = () => {
      setCallState('idle');
    };

    window.addEventListener('voice-incoming-call', onIncoming);
    window.addEventListener('voice-call-active', onActive);
    window.addEventListener('voice-call-ended', onEnded);

    return () => {
      window.removeEventListener('voice-incoming-call', onIncoming);
      window.removeEventListener('voice-call-active', onActive);
      window.removeEventListener('voice-call-ended', onEnded);
    };
  }, []);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(OPEN_CALL_PANEL_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_CALL_PANEL_EVENT, onOpen);
  }, []);

  const handleSaveConfig = async () => {
    if (!tenantId) {
      toast.error('Tenant is required to save call panel settings');
      return;
    }

    setSavingConfig(true);
    try {
      const configRef = doc(db, 'tenants', String(tenantId), 'settings', 'callPanel');
      await setDoc(configRef, {
        buttons: normalizeButtons(buttons),
        updatedAt: serverTimestamp(),
      }, { merge: true });
      toast.success('Call panel settings saved');
      setConfigOpen(false);
    } catch (error) {
      console.error('[CallPanel] Failed to save config', error);
      toast.error('Failed to save call panel settings');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleRunButton = async (btn) => {
    if (!tenantId) {
      toast.error('Tenant is required');
      return;
    }

    if (!btn?.flowId) {
      toast.error('No flow configured for this button');
      return;
    }

    const traceId =
      (globalThis.crypto?.randomUUID && globalThis.crypto.randomUUID()) ||
      `${Date.now()}_${Math.random().toString(16).slice(2)}`;

    const payload = {
      flowId: btn.flowId,
      tenantId,
      traceId,
      correlationId: traceId,
      source: 'call_panel',
      trigger: 'call_panel_button',
      callPanelButtonLabel: btn.label,
      context: {
        type: 'call',
        from_user: callContext.from_user || null,
        to_user: callContext.to_user || null,
      },
    };

    try {
      setRunningFlowId(btn.flowId);
      const res = await executeCommand(btn.flowId, payload);
      if (isFailedExecutionResult(res)) {
        throw new Error(flowDisabledUserMessage(null, res) || res.message || res.error || `Failed to run ${btn.label}`);
      }
      if (res?.executionStatus === 'waiting') {
        toast.info(waitingDisplayLabel(res.resumeAt || res.details?.resumeAt));
        return;
      }
      toast.success(`${btn.label} triggered`);
    } catch (error) {
      console.error('[CallPanel] Failed to run flow', error);
      toast.error(flowDisabledUserMessage(error) || error.message || `Failed to run ${btn.label}`);
    } finally {
      setRunningFlowId('');
    }
  };

  return (
    <>
      {hideFab ? null : (
        <Tooltip title="Call Panel" placement="left">
          <Badge
            color="error"
            variant="dot"
            invisible={callState !== 'incoming'}
            overlap="circular"
            sx={callState === 'incoming' ? {
              '& .MuiBadge-badge': {
                animation: 'callPanelPulse 1.2s infinite',
              },
              '@keyframes callPanelPulse': {
                '0%': { transform: 'scale(0.95)', opacity: 0.7 },
                '70%': { transform: 'scale(1.35)', opacity: 0.1 },
                '100%': { transform: 'scale(0.95)', opacity: 0.7 },
              },
            } : undefined}
          >
            <Fab
              color={fabColor}
              onClick={() => setOpen(true)}
              sx={{ position: 'fixed', right: 24, bottom: 24, zIndex: 1200 }}
              aria-label="Open call panel"
            >
              <PhoneIcon />
            </Fab>
          </Badge>
        </Tooltip>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <Typography variant="h6">📞 Call</Typography>
          <IconButton aria-label="Close call panel" onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              State: {callState}
            </Typography>

            <Stack direction="row" spacing={1}>
              <Button variant="contained" color="success" onClick={acceptCall} fullWidth>
                Answer
              </Button>
              <Button variant="outlined" color="error" onClick={hangupCall} fullWidth>
                Hang Up
              </Button>
            </Stack>

            <Box sx={{ pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Actions
              </Typography>
              <Stack spacing={1}>
                {buttons.map((btn, idx) => (
                  <Button
                    key={`${btn.label}-${idx}`}
                    variant="contained"
                    onClick={() => handleRunButton(btn)}
                    disabled={runningFlowId === btn.flowId}
                    fullWidth
                  >
                    {btn.label}
                  </Button>
                ))}
              </Stack>
            </Box>

            <Box sx={{ pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
              <Button size="small" onClick={() => setConfigOpen((prev) => !prev)}>
                {configOpen ? 'Hide Config' : 'Configure Buttons'}
              </Button>

              {configOpen && (
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {buttons.map((btn, idx) => (
                    <Box key={`config-${idx}`}>
                      <TextField
                        label={`Button ${idx + 1} Label`}
                        size="small"
                        fullWidth
                        value={btn.label}
                        onChange={(event) => {
                          const next = [...buttons];
                          next[idx] = { ...next[idx], label: event.target.value };
                          setButtons(next);
                        }}
                        sx={{ mb: 1 }}
                      />
                      <FormControl fullWidth size="small">
                        <InputLabel id={`call-panel-flow-${idx}`}>{`Button ${idx + 1} Flow`}</InputLabel>
                        <Select
                          labelId={`call-panel-flow-${idx}`}
                          label={`Button ${idx + 1} Flow`}
                          value={btn.flowId}
                          onChange={(event) => {
                            const next = [...buttons];
                            next[idx] = { ...next[idx], flowId: event.target.value };
                            setButtons(next);
                          }}
                        >
                          {loadingConfig && <MenuItem value={btn.flowId}>{btn.flowId}</MenuItem>}
                          {flowOptions.map((flow) => (
                            <MenuItem key={flow.id} value={flow.id}>{flow.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  ))}

                  <Button variant="outlined" onClick={handleSaveConfig} disabled={savingConfig}>
                    Save Config
                  </Button>
                </Stack>
              )}
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
